/**
 * DWG → DXF 변환 버튼 — 맵을 만들기 전에 쓰므로 맵 에디터 대시보드(맵 목록)에 있다.
 * DWG 를 CAD 저장소에 올리고 남길 레이어를 확인받아 정리된 DXF 를 같은 저장소에 만든다.
 * 맵을 만들 때 CAD 파일 목록에서 그 DXF 를 고르면 된다.
 */

import { useCallback, useRef, useState, type ChangeEvent } from 'react'
import { Button } from '@mui/material'
import { FileStorageService } from '../../../services/FileStorageService'
import { DwgCleanModal } from './DwgCleanModal'
import { DwgCleanCancelled, useDwgCleanStep } from './CSVUploader/hooks/useDwgCleanStep'

export interface DwgConvertButtonProps {
  siteProjectId: number | null
  onMessage: (kind: 'info' | 'success' | 'error', text: string) => void
}

export function DwgConvertButton({ siteProjectId, onMessage }: DwgConvertButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const handleProgress = useCallback((message: string) => onMessage('info', message), [onMessage])
  const { resolveDwg, dwgModal } = useDwgCleanStep(siteProjectId, handleProgress)

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 같은 파일을 다시 골라도 change 가 나게
    if (!file || !siteProjectId) return

    setBusy(true)
    onMessage('info', 'DWG 업로드 중...')
    try {
      const uploaded = await FileStorageService.uploadFiles(String(siteProjectId), 'cad', [file])
      const dwg = uploaded.success ? uploaded.data?.uploaded_files[0] : undefined
      if (!dwg) throw new Error('DWG 파일을 서버에 올리지 못했습니다.')
      const dxf = await resolveDwg(dwg)
      onMessage('success', `DXF 를 만들었습니다: ${dxf.original_name || dxf.filename} — 맵을 만들 때 CAD 파일 목록에서 고르세요.`)
    } catch (error) {
      if (error instanceof DwgCleanCancelled) {
        onMessage('info', 'DWG 변환을 취소했습니다.')
      } else {
        onMessage('error', error instanceof Error ? error.message : 'DWG 변환에 실패했습니다.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" accept=".dwg" onChange={handleFile} style={{ display: 'none' }} data-testid="dwg-convert-input" />
      <Button
        variant="outlined"
        size="small"
        onClick={() => inputRef.current?.click()}
        disabled={!siteProjectId || busy}
        title="DWG 를 올리면 사내 AutoCAD 로 맵에 필요한 레이어만 남긴 DXF 를 CAD 파일 목록에 만든다"
        sx={{ borderRadius: 2, display: { xs: 'none', sm: 'inline-flex' } }} // 휴대폰에서 DWG 를 다룰 일은 없다
      >
        {busy ? '⏳ DWG 변환 중...' : '📐 DWG → DXF 변환'}
      </Button>
      {dwgModal && <DwgCleanModal {...dwgModal} />}
    </>
  )
}
